import { useQuery } from '@apollo/client';
import React from 'react';
import { default as Form } from 'components/connectedForm';
import createForm from '../../components/connectedForm/ConnectedForm';
import LuckinsItem from '../../graphql/entities/product/LuckinsItem';
import { useDispatch, useSelector } from 'react-redux';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import OverviewCard from '../../components/connectedForm/OverviewCard';
import OverviewCardTitle from '../../components/connectedForm/OverviewCardTitle';
import OverviewCardDivider from '../../components/connectedForm/OverviewCardDivider';
import ValueCard from '../../components/connectedForm/ValueCard';
import OverviewContainer from 'components/connectedForm/OverviewContainer';
import LuckinsLiveItemQuery from 'graphql/queries/product/LuckinsLiveItemQuery.graphql';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { Button } from '../../components/connectedForm/Button';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import BreadCrumbs from '../../components/connectedForm/BreadCrumbs';
import { LuckinsTag, WarningPill } from '../../components/StatusPill/StatusPill';
import styled from 'styled-components';
import HeaderEntityValue from '../../components/connectedForm/HeaderEntityValue';
import H2 from '../../components/connectedForm/Heading2';
import Row from '../../components/connectedForm/Row';
import Fieldset from '../../components/connectedForm/Fieldset';
import { Image } from '@fluentui/react/lib/Image';
import { TextField } from '@fluentui/react/lib/TextField';
import { Label } from '@fluentui/react/lib/Label';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { useFormQuery } from '../../graphql/containers/useFormQuery';

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

const statusMap = {
  D: 'De-listed',
  Q: 'Query',
  V: 'Verified',
  R: 'Re-instated',
};

const TitleInfoTag = styled.div`
  display: block;
  font-size: medium;
  color: #b1b1b1;
`;

//Clone of BreadCrumbs.js 'Container'
const TagContainer = styled.div`
  padding: 8px 0 3px 20px;
  background-color: #faf9f8;
`;

const LuckinsItemHeaderBlock = (props) => {
  const { entityHdr, values, itemDetails } = props;
  const { [entityHdr]: value, productRangeMajor, productRangeMinor, brandName } = values || {};

  const thumbnailUrl = itemDetails ? itemDetails.thumbnailUrl || `/api/resources/image/default-no-image.png` : null;
  const tsiCode = itemDetails?.tsiItemCode;
  const statusDescription = statusMap[values?.status];

  const statusPill = statusDescription ? (
    <div style={{ display: 'inline-block', width: '120px', paddingLeft: '15px' }}>
      <WarningPill>{statusDescription}</WarningPill>
    </div>
  ) : null;

  return (
    <>
      <BreadCrumbs {...props} title={'Luckins Detail'} />
      <TagContainer>
        <LuckinsTag />
      </TagContainer>
      <HeaderContainer>
        <Row style={{ marginLeft: '0px' }}>
          {itemDetails ? (
            <Image src={thumbnailUrl} />
          ) : (
            <div
              style={{
                height: 100,
                width: 100,
                padding: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spinner size={SpinnerSize.large} />
            </div>
          )}
          <TagContainer>
            <H2>
              <Fieldset cols={1}>
                <HeaderEntityValue style={{ display: 'block' }}>
                  {value}
                  {statusPill}
                </HeaderEntityValue>
                <TitleInfoTag>{brandName}</TitleInfoTag>
                <TitleInfoTag>
                  {productRangeMajor} | {productRangeMinor}
                </TitleInfoTag>
                <TitleInfoTag>{`TSI Code: ${tsiCode}`}</TitleInfoTag>
              </Fieldset>
            </H2>
          </TagContainer>
        </Row>
        <HeaderAction>
          <Button onClick={props.onCancel}>Cancel</Button>
        </HeaderAction>
      </HeaderContainer>
    </>
  );
};

const AttributeEntry = ({ attributeEntry }) => {
  return (
    <div style={{ display: 'flex' }}>
      <Label style={{ display: 'inline-block' }}>{`${attributeEntry.description}:`}</Label>
      <TextField style={{ display: 'inline-block' }} value={attributeEntry.value} borderless={true} readOnly={true} />
    </div>
  );
};

const AttributeClass = ({ attributeClass }) => {
  return (
    <Row key={`_${attributeClass.id}`}>
      <Fieldset cols={1 / 4}>
        <Label>{attributeClass.description}</Label>
      </Fieldset>
      <Fieldset cols={3 / 4}>
        {attributeClass.attributeLabels.map((attribute, index) => (
          <AttributeEntry key={`_${index}`} attributeEntry={attribute} />
        ))}
      </Fieldset>
    </Row>
  );
};

const AttributesTab = ({ attributeClassList, attributeLabelList, itemDetails }) => {
  if (!attributeClassList || !attributeLabelList || !itemDetails) return null;

  const mappedAttributeClass = attributeClassList.map((attributeClass) => {
    return {
      ...attributeClass,
      attributeLabels: attributeLabelList
        .filter((attributeLabel) => attributeLabel.attributeClassId === attributeClass.id)
        .map((attributeLabel) => {
          return {
            ...attributeLabel,
            value: itemDetails.attributeList.find(
              (attributeValue) => attributeValue.attributeLabelId === attributeLabel.id
            )?.value,
          };
        }),
    };
  });

  return (
    <Form.Row>
      <Form.Fieldset cols={7 / 12}>
        <Form.Legend label="Attributes Information" />
        {mappedAttributeClass.map((attributeClass, index) => (
          <AttributeClass key={`class_${index}`} attributeClass={attributeClass} />
        ))}
      </Form.Fieldset>
    </Form.Row>
  );
};

const Asset = ({ asset }) => {
  switch (asset.fileType) {
    case 'FILETYPE_IMAGE':
      return (
        <Form.Fieldset cols={1 / 3}>
          <Label>{asset.additionalInfo}</Label>
          <Image src={asset.url} />
        </Form.Fieldset>
      );
    case 'FILETYPE_PDF':
      return (
        <Form.Fieldset cols={1 / 2}>
          <embed
            src={asset.url}
            key={`${asset.fileName}`}
            width={'100%'}
            height={'500px'}
            style={{ marginTop: '14px' }}
            alt="pdf"
            title={'PDF Asset'}
          />
        </Form.Fieldset>
      );
  }
  return (
    <Form.Fieldset cols={1 / 3}>
      <Label>{asset.additionalInfo}</Label>
    </Form.Fieldset>
  );
};

const AssetsTab = ({ itemDetails }) => {
  if (!itemDetails) return null;

  return (
    <Form.Row>
      <Form.Fieldset cols={1}>
        <Form.Legend label="Assets" />
      </Form.Fieldset>
      {itemDetails.asset2List.map((asset, index) => {
        return <Asset key={`asset_${index}`} asset={asset} />;
      })}
    </Form.Row>
  );
};

const ConnectedLuckinsItemForm = createForm({
  form: LuckinsItem.type,
  name: 'View Luckins Item',
  entity: LuckinsItem,
})((props) => {
  const { sidebar } = props; //Future-Proofing, potential capability to display LuckinsItem information in Sidebar.
  const cols = 1; //sidebar ? 1 : 1; //Sticking to 1 for the moment.
  const values = props.values || {};

  const { data: luckinsLiveData } = useQuery(LuckinsLiveItemQuery, {
    variables: { itemCode: values.itemCode },
    skip: !values.itemCode,
  });

  const { itemDetailsList, vatList, attributeClassList, attributeLabelList } = luckinsLiveData?.data || {};
  const itemDetails = itemDetailsList?.[0]; //Always one entry as we've queried by item code.

  const {
    catalogueNumber1,
    catalogueNumber2,
    commodityCode,
    vatRateCode,
    priceUnit,
    stockUnit,
    splitPackPrice,
    tradePrice1,
    tradePrice2,
    tradePrice3,
    tradePrice4,
  } = values;

  const vatRateDescription =
    vatList && vatRateCode ? vatList.find((vatEntry) => vatEntry.vatCode === vatRateCode)?.vatDescription : null;

  return (
    <Form.FormContainer>
      <Form.Header
        entityHdr="shortDescription"
        component={LuckinsItemHeaderBlock}
        noDeleteOption
        noSaveOption
        itemDetails={itemDetails}
      />
      <Form.Form>
        <Form.TabPanel>
          <Form.TabContent id="main" label="General">
            <OverviewContainer sidebar={sidebar}>
              <Form.Row>
                <Form.Fieldset cols={cols}>
                  <OverviewCard>
                    <OverviewCardTitle title="General" />
                    <Form.Row style={{ paddingRight: '10px' }}>
                      <Form.Fieldset cols={1 / 4}>
                        <ValueCard
                          readOnly={true}
                          value={catalogueNumber1}
                          label="Catalogue Number"
                          labelPosition={'top'}
                          type={'text'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 4}>
                        <ValueCard
                          readOnly={true}
                          value={catalogueNumber2}
                          label="Manufacturers Number"
                          labelPosition={'top'}
                          type={'text'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 4}>
                        <ValueCard
                          readOnly={true}
                          value={commodityCode}
                          label="Commodity"
                          labelPosition={'top'}
                          type={'text'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 4}>
                        <ValueCard
                          readOnly={true}
                          value={vatRateDescription}
                          label="VAT Rate"
                          labelPosition={'top'}
                          type={'text'}
                        />
                      </Form.Fieldset>
                    </Form.Row>
                  </OverviewCard>
                </Form.Fieldset>
                <Form.Fieldset cols={cols}>
                  <OverviewCard>
                    <OverviewCardTitle title="Prices" />
                    <Form.Row style={{ paddingRight: '10px' }}>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={priceUnit}
                          label="Pricing Unit "
                          decimals={2}
                          labelPosition={'top'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard readOnly={true} value={stockUnit} label="Stock Unit" labelPosition={'top'} />
                      </Form.Fieldset>
                      <OverviewCardDivider />
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={splitPackPrice?.price}
                          label="Split Pack Price"
                          decimals={2}
                          labelPosition={'top'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={tradePrice1?.price}
                          label="Trade Price 1"
                          decimals={2}
                          labelPosition={'top'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={tradePrice2?.price}
                          label="Trade Price 2"
                          decimals={2}
                          labelPosition={'top'}
                          secondaryLabel={
                            tradePrice2?.priceBreakQuantity ? `order ${tradePrice2.priceBreakQuantity}+` : null
                          }
                          secondaryLabelFontStyle={{ fontSize: '8px', fontWeight: 400 }}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={tradePrice3?.price}
                          label="Trade Price 3"
                          decimals={2}
                          labelPosition={'top'}
                          secondaryLabel={
                            tradePrice3?.priceBreakQuantity ? `order ${tradePrice3.priceBreakQuantity}+` : null
                          }
                          secondaryLabelFontStyle={{ fontSize: '8px', fontWeight: 400 }}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 6}>
                        <ValueCard
                          readOnly={true}
                          value={tradePrice4?.price}
                          label="Trade Price 4"
                          decimals={2}
                          labelPosition={'top'}
                          secondaryLabel={
                            tradePrice4?.priceBreakQuantity ? `order ${tradePrice4.priceBreakQuantity}+` : null
                          }
                          secondaryLabelFontStyle={{ fontSize: '8px', fontWeight: 400 }}
                        />
                      </Form.Fieldset>
                    </Form.Row>
                  </OverviewCard>
                </Form.Fieldset>
              </Form.Row>
            </OverviewContainer>
          </Form.TabContent>
          <Form.TabContent id="attributes" label="Attributes" shouldRender={itemDetails?.attributeList?.length > 0}>
            <AttributesTab
              attributeClassList={attributeClassList}
              attributeLabelList={attributeLabelList}
              itemDetails={itemDetails}
            />
          </Form.TabContent>
          <Form.TabContent id="relatedItems" label="Related Items">
            <Form.Row>
              <Form.Fieldset cols={7 / 12}>
                <Form.Legend label="Related Items" />
              </Form.Fieldset>
            </Form.Row>
          </Form.TabContent>
          <Form.TabContent id="assets" label="Assets">
            <AssetsTab itemDetails={itemDetails} />
          </Form.TabContent>
        </Form.TabPanel>
      </Form.Form>
    </Form.FormContainer>
  );
});

export default (props) => {
  const { overrideParams } = props;

  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const formData = useFormQuery({ instance: LuckinsItem.instance }, { overrideParams });
  const dispatch = useDispatch();

  return <ConnectedLuckinsItemForm {...formProps} {...props} formData={formData} dispatch={dispatch} />;
};
